import React, {useEffect} from "react";
import {
  InputGroup,
  InputLeftElement,
  Input,
  Box,
  VStack,
  Text,
  HStack,
  Divider,
  Tag,
  Avatar,
  Flex, InputRightElement,
} from "@chakra-ui/react";
import {useState, useRef} from "react";
import SmartIcon from "../SmartIcon.tsx";
import platform from "platform";
import KeyCombo from "../KeyCombo.tsx";
import {useProjectsStore} from "../../store/useProjectsStore.ts";
import {IProject} from "../../models/interfaces/project.interface.ts";
import useWorkspaceStore from "../../store/useWorkspaceStrore.ts";
import {IWorkspaceMember} from "../../models/interfaces/workspaceMember.interface.ts";
import {useProject} from "../../hooks/useProject.ts";
import {IStateEnum} from "../../models/enums/state.enum.ts";
import {camelCaseToTitleCase, snakeCaseToSentenceCase} from "../../utils/utils.ts";
import useUserStore from "../../store/useUserStore.ts";
import iso6391 from "iso-639-1";
import {IItemsSelect} from "../../models/interfaces/selectItems.interface.ts";
import {IGlobalSearchFilter} from "../../models/interfaces/globalSearchFilter.interface.ts";
import {twMerge} from "tailwind-merge";
import CreateBySelect from "./CreateBySelect.tsx";
import DateSelect from "./DateSelect.tsx";
import LanguagesSelect from "./LanguagesSelect.tsx";
import ProjectDrawer from "../ProjectDetails/ProjectDrawer.tsx";
import useUiStore from "../../store/useUiStore.ts";
import {DATE_FILTER, isProjectInDateFilter} from "./dateFilter.ts";

const DEFAULT_FILTER: IItemsSelect = {
  id: "all",
  title: "All"
}
const DEFAULT_DATE_FILTER: IItemsSelect[] = [
  {
    id: DATE_FILTER[0],
    title: DATE_FILTER[0]
  },
  {
    id: DATE_FILTER[3],
    title: DATE_FILTER[3]
  },
  {
    id: "older",
    title: "Older"
  },
]

const GlobalSearchInput: React.FC = () => {
  const {openDrawer} = useUiStore();
  const {user} = useUserStore();
  const {projects, projectsToView, filterProjectsByTerm} = useProjectsStore();
  const {members} = useWorkspaceStore();
  const {extractTargetTranslationsLanguages, getProjectStateEnum} = useProject();
  const [isFocused, setIsFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [assignToFilter, setAssignToFilter] = useState<IItemsSelect | null>(null); // State for Assign To id filter
  const [dateFilter, setDateFilter] = useState<IItemsSelect | null>(null); // State for Date filter
  const [languageFilter, setLanguageFilter] = useState<IItemsSelect | null>(null); // State for Language filter lang code
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const isMac: boolean | undefined = platform.os?.family?.includes("OS X");

    const handleKeyDown = (e: KeyboardEvent) => {
      const isCmdOrCtrl: boolean = isMac ? e.metaKey : e.ctrlKey;
      if (!isFocused && isCmdOrCtrl && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setIsFocused(true);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
      if (isFocused && e.key === "Escape") {
        e.preventDefault();
        setIsFocused(false);
        inputRef.current?.blur();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocused]);

  const filterOptions: IGlobalSearchFilter = {
    assignees: [DEFAULT_FILTER, ...Object.values(members).map((member: IWorkspaceMember) => ({
      id: member.userId,
      title: member.userId == user?._id ? "Me" : `${member.firstName} ${member.lastName}`,
    }))],
    dates: [DEFAULT_FILTER, ...DATE_FILTER.map((dateCategory: string) => ({
      id: dateCategory,
      title: snakeCaseToSentenceCase(dateCategory),
    }))],
    languages: [DEFAULT_FILTER, ...Array.from(
      new Set(
        projects.flatMap((project: IProject) => [
          project.sourceLanguage,
          ...extractTargetTranslationsLanguages(project),
        ])
      )
    ).map((languageCode: string) => ({
      id: languageCode,
      title: iso6391.getName(languageCode) || languageCode,
    }))],
  };

  const isAssigneeFilterMatch = (item: IProject): boolean => {
    if (!assignToFilter) return true;
    return item.createdBy === assignToFilter.id;
  }

  const isLanguageFilterMatch = (item: IProject): boolean => {
    if (!languageFilter) return true;
    return (
      item.sourceLanguage === languageFilter.id ||
      extractTargetTranslationsLanguages(item).includes(languageFilter.id)
    );
  }

  const isSearchTermMatch = (item: IProject): boolean => {
    if (!searchTerm) return true;
    const searchLower: string = searchTerm.toLowerCase();
    return item.name.toLowerCase().includes(searchLower);
  }

  const highlightMatch = (text: string, term: string) => {
    if (!term) return text;

    const regex = new RegExp(`(${term})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part: string, index: number) =>
      regex.test(part) ? (
        <span key={index} className="text-brandPurple-500">{part}</span> // brandPurple-500
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );
  };

  const onItemClick = (item: IProject) => {
    setIsFocused(false);
    openDrawer(<ProjectDrawer projectId={item.id}/>)
  }

  useEffect(() => {
    //   event listener on 'enter' key to trigger filterProjectsByTerm if searchTerm is not empty
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFocused && e.key === "Enter" && searchTerm.trim() !== "") {
        e.preventDefault();
        filterProjectsByTerm(searchTerm);
        setIsFocused(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFocused, searchTerm, filterProjectsByTerm]);

  useEffect(() => {
    if (projectsToView.length === 0) {
      setSearchTerm("");
    }
  }, [projectsToView]);

  return (
    <Box position="relative" w="100%">
      {/* Container for the Search Input Field with Dynamic Width */}
      <InputGroup
        w={isFocused ? "150%" : "100%"} // Dynamic width on focus
        transition="width 0.3s ease" // Smooth transition for width change
        position="relative"
        zIndex={isFocused ? 10 : 1}
      >
        <InputLeftElement
          pointerEvents="none"
          height="100%"
          display="flex"
          alignItems="center"
          pl={3}
        >
          <SmartIcon
            name="search"
            className="fill-n-400 transition-colors group-hover:fill-n-300"
          />
        </InputLeftElement>
        <Input
          ref={inputRef}
          className="bg-n-700 placeholder-n-500 dark:bg:n-500 dark:placeholder-n-400"
          pl="10"
          type="text"
          placeholder="Search projects"
          borderRadius="full"
          variant={isFocused ? "outline" : "filled"}
          onFocus={() => setIsFocused(true)}
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          zIndex={isFocused ? 10 : 1}
        />
        <InputRightElement
          height="100%"
          display="flex"
          alignItems="center"
          pr={3}
        >
          {isFocused
            ? <KeyCombo value={"ESC"}/>
            : <KeyCombo value={platform.os?.family?.includes("OS X") ? "⌘ F" : "Ctrl F"}/>
          }
        </InputRightElement>
      </InputGroup>

      {/* Dropdown for results and filters */}
      {isFocused && (
        <Box
          ref={dropdownRef}
          className={twMerge(isFocused ? "w-[150%]" : "w-full", "global-search-dropdown-wrapper")}
        >
          {/* Filter Header with Select Lists */}
          <HStack justify="space-between" mb={3}>
            <HStack spacing={4} w="full">
              {/* Create By Dropdown */}
              <CreateBySelect
                assignees={filterOptions.assignees}
                value={assignToFilter}
                onChange={setAssignToFilter}
              />
              <DateSelect
                dates={filterOptions.dates}
                value={dateFilter}
                onChange={setDateFilter}
              />
              <LanguagesSelect
                languages={filterOptions.languages}
                value={languageFilter}
                onChange={setLanguageFilter}
              />
            </HStack>
          </HStack>

          <Divider className="dark:bg:n-400/50"/>

          {/* Grouped Search Results */}
          <VStack align="stretch" mt={3} spacing={4}>
            {(dateFilter ? [dateFilter] : DEFAULT_DATE_FILTER)
              .map((dateGroup: IItemsSelect) => (
                <Box key={dateGroup.id}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1}>
                    {snakeCaseToSentenceCase(dateGroup.title)}
                  </Text>
                  {(() => {
                    const filtered: IProject[] = projects
                      .filter((item: IProject) => isAssigneeFilterMatch(item))
                      .filter((item: IProject) => isProjectInDateFilter(item, dateGroup))
                      .filter((item: IProject) => isLanguageFilterMatch(item))
                      .filter((item: IProject) => isSearchTermMatch(item))

                    return filtered.length > 0 ? (
                      filtered.map((item: IProject, index: number) => {
                        const assignee: IWorkspaceMember | undefined = Object.values(members).find((member: IWorkspaceMember) => member.userId === item.createdBy);
                        const stateEnum: IStateEnum = getProjectStateEnum(item);
                        return (
                          <HStack
                            key={index}
                            className="items-center p-3 rounded-lg dark:hover:bg-n-500 cursor-pointer"
                            spacing={4}
                            overflow="hidden"
                            onClick={() => onItemClick(item)}
                          >
                            <Avatar
                              name={`${assignee?.firstName} ${assignee?.lastName}`}
                              src={assignee?.imageSasLink}
                              size="sm"
                              bg="purple.500"
                              color="white"
                            />
                            <Box flex="1" overflow="hidden">
                              <Text fontWeight="bold" color="whiteAlpha.900" isTruncated maxW="400px">
                                {highlightMatch(item.name, searchTerm)}
                              </Text>
                              <Flex
                                wrap="wrap"
                                gap={2}
                                fontSize="xs"
                                color="whiteAlpha.700"
                                align="center"
                                overflow="hidden"
                              >
                                <Text isTruncated maxW="100px">
                                  {item.priority}
                                </Text>
                                {[item.sourceLanguage, ...extractTargetTranslationsLanguages(item)].map((lang: string) => (
                                  <Tag key={lang} size="sm" colorScheme="green" flexShrink={0}>
                                    {lang}
                                  </Tag>
                                ))}
                              </Flex>
                            </Box>
                            <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                              {item.updatedAt === "Today" ? "Today" : "25/08/2024"}
                            </Text>
                            <Tag
                              size="sm"
                              colorScheme={
                                stateEnum === IStateEnum.DONE
                                  ? "green"
                                  : stateEnum === IStateEnum.FAILED
                                    ? "red"
                                    : stateEnum === IStateEnum.PENDING
                                      ? "purple"
                                      : "orange"
                              }
                              maxW="80px"
                              isTruncated
                            >
                              {camelCaseToTitleCase(stateEnum)}
                            </Tag>
                          </HStack>
                        );
                      })
                    ) : (
                      <Text color="gray.500" textAlign="center" p={3}>
                        No projects found for this date range.
                      </Text>
                    );
                  })()}
                </Box>
              ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default GlobalSearchInput;
